import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import UiIcon from './icons/UiIcon';
import { statusBadgeClass, statusLabel } from '../utils/rmc2';

/**
 * The small shared surfaces every RMC2 screen is built from, ported from the
 * prototype's `common/UIComponents.tsx`.
 *
 * The prototype wrote its navy and gold as inline hexes and Tailwind neutrals
 * (`bg-white`, `text-slate-500`). Those are correct in exactly one theme, so
 * everything here is re-expressed against the `index.css` tokens instead — same
 * design, legible in both (Rule 56). The layout, spacing and hierarchy are the
 * prototype's and are deliberately unchanged.
 *
 * Action buttons are NOT here: those go through the bundled `<Button>`.
 */

/** Status pill. The tone map lives in `utils/rmc2.js` so a new status is one edit. */
export function Badge({ status, className = '' }) {
  if (!status) return null;
  // The server's enum value drives the tone; only the label is translated.
  return <span className={`${statusBadgeClass(status)} ${className}`}>{statusLabel(status)}</span>;
}

/** Tinted square behind a glyph — the prototype's IconCircle. */
export function IconChip({ children, tone = 'brand', size = '2.5rem' }) {
  const tones = {
    brand:   { background: 'var(--brand-soft)',   color: 'var(--brand)' },
    gold:    { background: 'var(--gold-soft)',    color: 'var(--gold-strong)' },
    success: { background: 'var(--success-soft)', color: 'var(--success)' },
    warn:    { background: 'var(--warn-soft)',    color: 'var(--warn)' },
    danger:  { background: 'var(--danger-soft)',  color: 'var(--danger)' },
    info:    { background: 'var(--info-soft)',    color: 'var(--info)' },
  };

  return (
    <span className="rmc2-icon-chip" style={{ width: size, height: size, ...(tones[tone] ?? tones.brand) }}>
      {children}
    </span>
  );
}

/**
 * KPI tile. `accent` colours the bottom bar so a row of tiles is scannable by
 * colour before it is read.
 */
export function StatCard({ title, value, subtitle, icon, accent = 'navy', onClick }) {
  const body = (
    <>
      <p className="rmc2-stat-label">{title}</p>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '0.75rem' }}>
        <h3 className="rmc2-stat-value">{value}</h3>
        {icon && <IconChip>{icon}</IconChip>}
      </div>
      {subtitle && <p className="rmc2-stat-sub">{subtitle}</p>}
    </>
  );

  // A clickable tile is a link into the filtered queue behind the number —
  // the fastest path from "12 awaiting verification" to acting on them.
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`rmc2-stat rmc2-stat-${accent}`}
        style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit', width: '100%' }}
      >
        {body}
      </button>
    );
  }

  return <div className={`rmc2-stat rmc2-stat-${accent}`}>{body}</div>;
}

/** Card + optional header row. `actions` sits opposite the title. */
export function Card({ title, subtitle, actions, children, className = '', bodyClassName = '' }) {
  return (
    <section className={`rmc2-card ${className}`}>
      {(title || actions) && (
        <header
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem',
            padding: 'clamp(0.85rem, 3vw, 1.15rem) clamp(1rem, 3vw, 1.5rem)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div>
            {title && <h2 className="rmc2-section-title">{title}</h2>}
            {subtitle && <p className="rmc2-section-sub">{subtitle}</p>}
          </div>
          {actions && <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>{actions}</div>}
        </header>
      )}
      <div className={`rmc2-card-pad ${bodyClassName}`}>{children}</div>
    </section>
  );
}

/** Page heading. Every in-shell page opens with one so the tab is identifiable. */
export function PageHeader({ title, subtitle, actions }) {
  return (
    <header
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: '0.75rem',
        marginBottom: '1.25rem',
      }}
    >
      <div>
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-subtitle">{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>{actions}</div>}
    </header>
  );
}

/** Labelled form control wrapper with hint + error wiring (jsx-a11y clean). */
export function Field({ label, required, hint, error, htmlFor, children }) {
  const hintId = hint ? `${htmlFor}-hint` : undefined;
  const errId = error ? `${htmlFor}-err` : undefined;

  return (
    <div className="rmc2-field">
      <label className="rmc2-label" htmlFor={htmlFor}>
        {label}
        {required && <span className="rmc2-req" aria-hidden="true"> *</span>}
      </label>
      {typeof children === 'function'
        ? children({
            id: htmlFor,
            'aria-invalid': error ? 'true' : undefined,
            'aria-describedby': [errId, hintId].filter(Boolean).join(' ') || undefined,
          })
        : children}
      {hint && !error && <p id={hintId} className="rmc2-hint">{hint}</p>}
      {error && <p id={errId} className="rmc2-err" role="alert">{error}</p>}
    </div>
  );
}

/** Responsive form grid — one column on a phone, `cols` from 40rem up. */
export function FormGrid({ cols = 2, children }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: '1rem',
        gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${cols === 1 ? '100%' : '15rem'}), 1fr))`,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Empty state. Always carries the next action — an empty list that only says
 * "no records" is a dead end, and dead ends are where people give up.
 */
export function EmptyState({ title, description, action }) {
  return (
    <div className="rmc2-empty" role="status">
      <p className="rmc2-empty-title">{title}</p>
      {description && <p className="rmc2-empty-desc">{description}</p>}
      {action}
    </div>
  );
}

/** Error state with a retry — the recovery action Rule 36 requires. */
export function ErrorState({ message, onRetry }) {
  const { t } = useTranslation();

  return (
    <div className="rmc2-empty" role="alert">
      <p className="rmc2-empty-title" style={{ color: 'var(--danger)' }}>{t('rmc2.error.title')}</p>
      <p className="rmc2-empty-desc">{message || t('rmc2.error.generic')}</p>
      {onRetry && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>
          <span className="btn-label">{t('rmc2.error.retry')}</span>
        </button>
      )}
    </div>
  );
}

/** Segmented filter pills — the queue selector on every admin tab. */
export function PillFilter({ options, value, onChange, ariaLabel }) {
  return (
    <div className="rmc2-pills" role="group" aria-label={ariaLabel}>
      {options.map((opt) => {
        const key = typeof opt === 'string' ? opt : opt.value;
        const label = typeof opt === 'string' ? opt : opt.label;
        const count = typeof opt === 'string' ? undefined : opt.count;

        return (
          <button
            key={key ?? 'all'}
            type="button"
            aria-pressed={value === key}
            className={`rmc2-pill ${value === key ? 'is-active' : ''}`}
            onClick={() => onChange(key)}
          >
            {label}
            {count !== undefined && count !== null && (
              <span style={{ marginLeft: '0.35rem', opacity: 0.75 }}>({count})</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** Read-only label → value row, for detail panes and confirmation summaries. */
export function DetailRow({ label, value, mono }) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem 1rem',
        justifyContent: 'space-between',
        padding: '0.45rem 0',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <span style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>{label}</span>
      <span
        style={{
          fontSize: '0.82rem',
          fontWeight: 500,
          color: 'var(--text)',
          fontFamily: mono ? 'var(--font-mono)' : undefined,
          textAlign: 'right',
        }}
      >
        {value ?? '—'}
      </span>
    </div>
  );
}

/**
 * Modal dialog with the prototype's navy header bar.
 *
 * Closes on ESC and on backdrop click, restores focus to whatever opened it,
 * and locks body scroll while open. Focus moves to the panel on mount rather
 * than to the first field, so a screen reader hears the title before the form.
 */
export function Modal({ open, onClose, title, children, footer, maxWidth = '32rem' }) {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const restoreRef = useRef(null);

  const handleKey = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return undefined;

    // Captured now, not read in the cleanup: by teardown time `panelRef.current`
    // is already null, so the containment check below would never fire.
    const panel = panelRef.current;
    const restoreTo = document.activeElement;
    const prevOverflow = document.body.style.overflow;

    restoreRef.current = restoreTo;
    document.body.style.overflow = 'hidden';
    panel?.focus();

    return () => {
      document.body.style.overflow = prevOverflow;
      // Only pull focus back if it is still inside the modal we are closing;
      // otherwise we would yank it away from whatever the user moved to.
      if (restoreTo instanceof HTMLElement && panel?.contains(document.activeElement)) {
        restoreTo.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className="modal-overlay"
      role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      onKeyDown={handleKey}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="modal-panel"
        style={{ maxWidth, width: '100%', padding: 0, outline: 'none' }}
      >
        <div className="rmc2-modal-head">
          <h2 className="rmc2-modal-title">{title}</h2>
          <button type="button" className="rmc2-modal-close" onClick={onClose} aria-label={t('rmc2.closeDialog')}>
            <UiIcon name="close" className="h-4 w-4" />
          </button>
        </div>

        <div className="rmc2-modal-body">{children}</div>

        {footer && <div className="rmc2-modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Inline notice — the non-blocking alternative to a modal for warnings and
 * confirmations that do not need to stop the user.
 */
export function Notice({ tone = 'info', title, children }) {
  const tones = {
    info:    { bg: 'var(--info-soft)',    fg: 'var(--info)' },
    success: { bg: 'var(--success-soft)', fg: 'var(--success)' },
    warn:    { bg: 'var(--warn-soft)',    fg: 'var(--warn)' },
    danger:  { bg: 'var(--danger-soft)',  fg: 'var(--danger)' },
  };
  const t = tones[tone] ?? tones.info;

  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      style={{
        background: t.bg,
        borderLeft: `3px solid ${t.fg}`,
        borderRadius: 'var(--radius-sm)',
        padding: '0.7rem 0.9rem',
        fontSize: '0.82rem',
        color: 'var(--text)',
      }}
    >
      {title && <strong style={{ display: 'block', color: t.fg, marginBottom: '0.15rem' }}>{title}</strong>}
      {children}
    </div>
  );
}
